import { IPositionWithCandidates } from '@types';
import { isInactiveCandidate as isInactive } from './candidate';

export const countInactiveCandidates = (
  positions: IPositionWithCandidates[],
): number =>
  mergeDuplicatePositions(positions).reduce(
    (total, position) => total + position.candidates.filter(isInactive).length,
    0,
  );

export const filterInactiveCandidates = (
  positions: IPositionWithCandidates[],
): IPositionWithCandidates[] => {
  return positions.map((position) => ({
    ...position,
    hiddenInactiveCandidatesCount:
      position.candidates.filter(isInactive).length,
    candidates: position.candidates.filter(
      (candidate) => !isInactive(candidate),
    ),
  }));
};

const buildPositionGroupingKey = (
  position: IPositionWithCandidates,
): string => {
  if (position.groupId?.trim()) return `group:${position.groupId.trim()}`;

  const name = position.name?.trim() ?? '';
  const workstream = position.workstream?.trim() ?? '';
  return `name:${workstream}:${name}`;
};

export const mergeDuplicatePositions = (
  positions: IPositionWithCandidates[],
): IPositionWithCandidates[] => {
  const grouped = new Map<string, IPositionWithCandidates>();

  positions.forEach((position) => {
    const key = buildPositionGroupingKey(position);
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        ...position,
        spotsCount: 1,
        candidates: [...position.candidates],
      });
      return;
    }

    const mergedCandidates = new Map(
      existing.candidates.map((candidate) => [candidate.id, candidate]),
    );
    position.candidates.forEach((candidate) => {
      if (!mergedCandidates.has(candidate.id)) {
        mergedCandidates.set(candidate.id, candidate);
      }
    });

    const mergedSeniorities = Array.from(
      new Set([
        ...(existing.seniorities ?? []),
        ...(position.seniorities ?? []),
      ]),
    );

    grouped.set(key, {
      ...existing,
      link: existing.link ?? position.link,
      seniorities: mergedSeniorities,
      candidates: Array.from(mergedCandidates.values()),
      hiddenInactiveCandidatesCount:
        (existing.hiddenInactiveCandidatesCount ?? 0) +
        (position.hiddenInactiveCandidatesCount ?? 0),
      spotsCount: (existing.spotsCount ?? 1) + 1,
    });
  });

  return Array.from(grouped.values());
};

const countActive = (position: IPositionWithCandidates): number =>
  position.candidates.filter((candidate) => !isInactive(candidate)).length;

export const sortPositionsByCandidates = (
  positions: IPositionWithCandidates[],
): IPositionWithCandidates[] => {
  return [...positions].sort((a, b) => {
    const candidateDiff = countActive(b) - countActive(a);
    if (candidateDiff !== 0) return candidateDiff;

    return (a.name ?? '').localeCompare(b.name ?? '');
  });
};

export const countActiveCandidates = (
  positions: IPositionWithCandidates[],
): number =>
  mergeDuplicatePositions(filterInactiveCandidates(positions)).reduce(
    (total, position) => total + position.candidates.length,
    0,
  );
